/**
 * LiveDemoPage Component
 * Immersive split-panel design with interactive selection
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { JobType } from "../types";
import { JOB_TYPE_CONFIGS } from "../services/jobTypes";
import { WebcamCapture } from "../components/camera/MediaCapture";
import { LiveStreamDemo } from "../components/camera/LiveStreamDemo";
import {
  ArrowLeft,
  Video,
  Camera,
  Play,
  Users,
  Car,
  ParkingCircle,
  ArrowLeftRight,
  Utensils,
  BedDouble,
  Building2,
  Sparkles,
  Zap,
  Clock,
  CheckCircle2,
  ChevronRight,
  Radio,
  Image,
} from "lucide-react";

type Step = "select-job" | "select-mode" | "capture" | "live-demo";

// Standard jobs that support webcam
const WEBCAM_ENABLED_JOBS: JobType[] = [
  JobType.PEOPLE_COUNTING,
  JobType.NUMBER_PLATE_DETECTION,
  JobType.PARKING_ANALYSIS,
  JobType.IN_OUT_COUNTING,
  JobType.FOOD_WASTE_ESTIMATION,
  JobType.ROOM_READINESS,
  JobType.LOBBY_CROWD_DETECTION,
];

// Icon mapping for job types
const JOB_TYPE_ICONS: Record<string, React.ReactNode> = {
  people_counting: <Users className="w-6 h-6" />,
  number_plate_detection: <Car className="w-6 h-6" />,
  parking_analysis: <ParkingCircle className="w-6 h-6" />,
  in_out_counting: <ArrowLeftRight className="w-6 h-6" />,
  food_waste_estimation: <Utensils className="w-6 h-6" />,
  room_readiness: <BedDouble className="w-6 h-6" />,
  lobby_crowd_detection: <Building2 className="w-6 h-6" />,
};

// Large icons for preview
const JOB_TYPE_ICONS_LG: Record<string, React.ReactNode> = {
  people_counting: <Users className="w-16 h-16" />,
  number_plate_detection: <Car className="w-16 h-16" />,
  parking_analysis: <ParkingCircle className="w-16 h-16" />,
  in_out_counting: <ArrowLeftRight className="w-16 h-16" />,
  food_waste_estimation: <Utensils className="w-16 h-16" />,
  room_readiness: <BedDouble className="w-16 h-16" />,
  lobby_crowd_detection: <Building2 className="w-16 h-16" />,
};

export const LiveDemoPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("select-job");
  const [selectedJobType, setSelectedJobType] = useState<JobType | null>(null);
  const [hoveredJobType, setHoveredJobType] = useState<JobType | null>(null);

  const handleSelectJobType = (jobType: JobType) => {
    setSelectedJobType(jobType);
    setStep("select-mode");
  };

  const handleSelectMode = (selectedMode: "capture" | "live-demo") => {
    setStep(selectedMode);
  };

  const handleBack = () => {
    if (step === "select-mode") {
      setStep("select-job");
      setSelectedJobType(null);
    } else if (step === "capture" || step === "live-demo") {
      setStep("select-mode");
    } else {
      navigate("/dashboard");
    }
  };

  const selectedConfig = selectedJobType ? JOB_TYPE_CONFIGS[selectedJobType] : null;
  const previewJobType = hoveredJobType || selectedJobType || WEBCAM_ENABLED_JOBS[0];
  const previewConfig = JOB_TYPE_CONFIGS[previewJobType];

  // Full screen camera modes
  if (step === "capture" && selectedJobType) {
    return (
      <div className="min-h-screen bg-navy-900">
        <div className="container mx-auto px-4 py-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Mode Selection
          </button>
          <WebcamCapture jobType={selectedJobType} />
        </div>
      </div>
    );
  }

  if (step === "live-demo" && selectedJobType) {
    return (
      <div className="min-h-screen bg-navy-900">
        <div className="container mx-auto px-4 py-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Mode Selection
          </button>
          <LiveStreamDemo jobType={selectedJobType} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-900">
      {/* Step 1: Select Analysis Type - Split Panel Design */}
      {step === "select-job" && (
        <div className="h-screen overflow-hidden flex">
          {/* Left Panel - Preview */}
          <div className="hidden lg:flex lg:w-[45%] h-full relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-primary-500/20 rounded-full blur-[100px]" />
              <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-accent-500/20 rounded-full blur-[80px]" />
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col h-full w-full p-8 xl:p-12">
              {/* Back Button */}
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors w-fit flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>

              {/* Preview Content */}
              <div className="flex-1 flex flex-col justify-center min-h-0">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500/10 to-accent-500/10 border border-primary-500/20 rounded-full px-4 py-2 mb-4 w-fit">
                  <Sparkles className="w-4 h-4 text-primary-400" />
                  <span className="text-sm font-medium text-primary-300">Live Demo</span>
                </div>

                {/* Large Icon */}
                <div className="w-24 h-24 xl:w-28 xl:h-28 rounded-2xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center mb-6 border border-primary-500/30">
                  <span className="text-primary-400">
                    {JOB_TYPE_ICONS_LG[previewJobType] || <Video className="w-12 h-12" />}
                  </span>
                </div>

                <h2 className="text-3xl xl:text-4xl font-bold text-white mb-3">
                  {previewConfig?.label || "Select Analysis"}
                </h2>
                <p className="text-base text-slate-400 mb-6 max-w-md">
                  {previewConfig?.description || "Choose an analysis type to get started"}
                </p>

                {/* Features */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-slate-300 text-sm">
                    <Zap className="w-4 h-4 text-primary-400" />
                    <span>Real-time AI processing</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300 text-sm">
                    <Camera className="w-4 h-4 text-primary-400" />
                    <span>Use your camera or webcam</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300 text-sm">
                    <Clock className="w-4 h-4 text-primary-400" />
                    <span>Results in seconds</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Selection List (No Scroll) */}
          <div className="w-full lg:w-[55%] h-full overflow-hidden bg-slate-900/50 flex flex-col">
            {/* Mobile Header */}
            <div className="lg:hidden p-6 pb-4 flex-shrink-0">
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <Video className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Live Demo</h1>
                  <p className="text-sm text-slate-400">Select analysis type</p>
                </div>
              </div>
            </div>

            {/* Desktop Title */}
            <div className="hidden lg:block p-8 xl:p-12 pb-4 flex-shrink-0">
              <h1 className="text-2xl font-bold text-white mb-2">Choose Analysis Type</h1>
              <p className="text-slate-400">Select the AI analysis you want to demonstrate</p>
            </div>

            {/* Analysis Options - Flex grow to fill remaining space */}
            <div className="flex-1 px-6 lg:px-8 xl:px-12 pb-6 lg:pb-8 xl:pb-12 min-h-0 flex flex-col gap-2">
              {WEBCAM_ENABLED_JOBS.map((jobType) => {
                const config = JOB_TYPE_CONFIGS[jobType];
                const icon = JOB_TYPE_ICONS[jobType] || <Video className="w-5 h-5" />;
                const isHovered = hoveredJobType === jobType;

                return (
                  <button
                    key={jobType}
                    onClick={() => handleSelectJobType(jobType)}
                    onMouseEnter={() => setHoveredJobType(jobType)}
                    onMouseLeave={() => setHoveredJobType(null)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left flex-1 min-h-0 ${
                      isHovered
                        ? "bg-gradient-to-r from-primary-500/10 to-accent-500/10 border-primary-500/50"
                        : "bg-slate-800/50 border-slate-700/50 hover:border-slate-600"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                      isHovered
                        ? "bg-gradient-to-br from-primary-500 to-accent-500 text-white"
                        : "bg-slate-700/50 text-slate-400"
                    }`}>
                      {icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white text-sm">{config.label}</h3>
                      <p className="text-xs text-slate-400 truncate">{config.description}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-all ${
                      isHovered ? "text-primary-400 translate-x-0.5" : "text-slate-500"
                    }`} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Select Mode - Full Width Cards */}
      {step === "select-mode" && selectedConfig && (
        <div className="h-screen overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm">
            <div className="container mx-auto px-6 py-4">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Analysis Selection
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-5xl">
              {/* Selected Analysis Badge */}
              <div className="flex items-center justify-center gap-3 mb-10">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center text-primary-400">
                  {JOB_TYPE_ICONS[selectedJobType!]}
                </div>
                <div className="text-center">
                  <span className="text-sm text-slate-400">Selected Analysis</span>
                  <h2 className="text-xl font-bold text-white">{selectedConfig.label}</h2>
                </div>
              </div>

              {/* Mode Selection Title */}
              <div className="text-center mb-10">
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3">
                  How would you like to demo?
                </h1>
                <p className="text-slate-400 max-w-xl mx-auto">
                  Choose between capturing a single image/video or running live real-time analysis
                </p>
              </div>

              {/* Mode Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Capture Mode */}
                <button
                  onClick={() => handleSelectMode("capture")}
                  className="group relative overflow-hidden rounded-2xl border border-slate-700/50 hover:border-primary-500/50 bg-slate-800/50 hover:bg-slate-800/80 p-8 text-left transition-all"
                >
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="relative">
                    {/* Icon */}
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center mb-6 shadow-lg shadow-primary-500/20 group-hover:scale-110 transition-transform">
                      <Image className="w-8 h-8 text-white" />
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-white mb-2">Capture Mode</h3>
                    <p className="text-slate-400 mb-6">
                      Take a photo or record a short video, then submit for AI analysis
                    </p>

                    {/* Features */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Take photos or record videos (60-90s)</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Review and retake before submitting</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Creates permanent job record</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="absolute top-8 right-8">
                      <ChevronRight className="w-6 h-6 text-slate-500 group-hover:text-primary-400 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </button>

                {/* Live Demo Mode */}
                <button
                  onClick={() => handleSelectMode("live-demo")}
                  className="group relative overflow-hidden rounded-2xl border border-slate-700/50 hover:border-accent-500/50 bg-slate-800/50 hover:bg-slate-800/80 p-8 text-left transition-all"
                >
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-accent-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="relative">
                    {/* Icon with pulse effect */}
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-500 to-purple-600 flex items-center justify-center mb-6 shadow-lg shadow-accent-500/20 group-hover:scale-110 transition-transform">
                        <Radio className="w-8 h-8 text-white" />
                      </div>
                      {/* Pulse indicator */}
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full animate-pulse" />
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-white mb-2">Live Demo Mode</h3>
                    <p className="text-slate-400 mb-6">
                      Real-time analysis with live overlay showing AI detections as they happen
                    </p>

                    {/* Features */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Real-time processing (1 frame/sec)</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Live bounding boxes and counts</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Perfect for presentations</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="absolute top-8 right-8">
                      <ChevronRight className="w-6 h-6 text-slate-500 group-hover:text-accent-400 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
